import Phaser from "phaser";
import { artLayoutLevel, isArtLayoutCapture } from "../../artLayout";
import { characterTexture } from "../assets";
import { applyBoardAction, getCell, mergeablePieceIds, type Board, type BoardAction, type GridPos } from "../model/board";
import { createStarterBoard } from "../model/starterBoard";
import { beginBattle, claimReward, createOpponentBoard, createRun, rewardOffers, type Reward, type Run } from "../model/run";
import { clearRun, saveRun } from "../persistence";
import { setGameStatus } from "../status";
import { drawBattlefield, drawDiamond, layoutFor, makeTeam, pieceDepth } from "../view/BattlefieldView";
import type { Side } from "../model/battle";
import { BattleButton, icon, PictureButton, PortraitCard, teamDock } from "../view/Controls";
import { Effects } from "../view/Effects";
import { hitBoardTarget, pieceMetrics, project } from "../view/iso";
import { TeamStatusView } from "../view/TeamStatusView";
import { PieceView } from "../view/PieceView";
import { motionEnabled } from "../view/preferences";
import { gameAudio } from "../view/Audio";
import { stageX, stageY } from "../view/stage";
import { openOnlineChat, requestLeaveMatch } from "../view/OnlineOverlay";
import { multiplayer } from "../multiplayer";

const sameCell = (a: GridPos | null, b: GridPos | null) => a?.x === b?.x && a?.y === b?.y;

export class BoardScene extends Phaser.Scene {
  private run!: Run;
  private pieces = new Map<string, PieceView>();
  private opponents = new Map<string, PieceView>();
  private remoteBoard?: Board;
  private remoteTarget?: Board;
  private remoteQueue: Board[] = [];
  private remoteAnimating = false;
  private teams?: TeamStatusView;
  private selected: GridPos | null = null;
  private hovered: GridPos | null = null;
  private busy = false;
  private selection!: Phaser.GameObjects.Graphics;
  private dock!: Phaser.GameObjects.Container;
  private battleButton?: BattleButton;
  private cards: PortraitCard[] = [];
  private effects!: Effects;
  private motion = true;
  private resetArmed = false;
  private arrivingId?: string;
  private multiplayerMode = false;
  private lockedReady = false;
  private networkUnsubscribe?: () => void;
  private ownSide: Side = "player";
  private get ownLayout() { return layoutFor(this, this.ownSide); }
  constructor() { super("BoardScene"); }
  init(data?: { run?: Run; arrivingId?: string; online?: boolean }): void {
    this.run = data?.run ?? { ...createRun("fire"), phase: "board", playerBoard: createStarterBoard() };
    this.arrivingId = data?.arrivingId;
    this.multiplayerMode = Boolean(data?.online);
    this.ownSide = this.multiplayerMode ? multiplayer.match!.ownSide : "player";
    this.lockedReady = this.multiplayerMode && Boolean(multiplayer.match?.own.ready);
    if (isArtLayoutCapture) {
      this.run = { ...this.run, level: artLayoutLevel, playerBoard: createStarterBoard(), opponentBoard: createOpponentBoard(8) };
      const seen = { fire: 0, ice: 0, lightning: 0 };
      this.run.playerBoard.cells.forEach(piece => { if (piece) piece.tier = 1 + seen[piece.element]++ * 2; });
    }
    this.remoteBoard = this.remoteTarget = undefined; this.remoteQueue = []; this.remoteAnimating = false; this.teams = undefined;
    this.selected = this.hovered = null;
    this.busy = this.resetArmed = false;
    this.motion = !isArtLayoutCapture && motionEnabled();
    this.pieces = new Map(); this.opponents = new Map(); this.cards = []; this.battleButton = undefined;
  }
  create(): void {
    drawBattlefield(this, this.run.level);
    this.effects = new Effects(this, this.motion);
    this.selection = this.add.graphics().setDepth(3);
    this.syncPieces();
    this.pieces.get(this.arrivingId ?? "")?.appear();
    if (isArtLayoutCapture) this.opponents = makeTeam(this, this.run.opponentBoard, "opponent", true, false);
    else if (this.multiplayerMode) {
      this.syncOpponents();
      this.teams = new TeamStatusView(this, this.effects, this.motion, multiplayer.match!);
    }
    if (!isArtLayoutCapture) {
      this.dock = teamDock(this, this.run.playerBoard);
      this.drawControls();
      const sound = new PictureButton(this, 1380, 66, gameAudio.enabled ? "speaker" : "muted", "Toggle sound", () => {
        gameAudio.toggle(); sound.setIcon(gameAudio.enabled ? "speaker" : "muted");
      });
      if (this.multiplayerMode) new PictureButton(this, 1290, 66, "chat", "Open chat", openOnlineChat, 60);
      let resetCaption: Phaser.GameObjects.Text | undefined;
      const reset = new PictureButton(this, 1470, 66, this.multiplayerMode ? "exit" : "reset", this.multiplayerMode ? "Leave multiplayer match" : "Start a new run; press twice to confirm", () => {
        if (this.multiplayerMode) { requestLeaveMatch(); return; }
        if (this.busy || this.lockedReady) return;
        if (!this.resetArmed) {
          this.resetArmed = true; reset.setIcon("check"); reset.setActive(true);
          reset.setLabel("Start Over");
          resetCaption = this.add.text(stageX(1506), stageY(115), "Start Over", { fontFamily: "Georgia, serif", fontSize: "26px",
            color: "#fff0cf", backgroundColor: "#29473e", padding: { x: 18, y: 10 } })
            .setOrigin(1, 0).setDepth(660).setInteractive({ useHandCursor: true });
          resetCaption.on("pointerdown", () => { if (!this.busy && !this.lockedReady) { clearRun(); if (this.multiplayerMode) multiplayer.leave(); this.scene.start("StartScene"); } });
          this.time.delayedCall(3500, () => {
            if (!reset.root.active) return;
            this.resetArmed = false; reset.setIcon("reset"); reset.setActive(false);
            reset.setLabel("Start a new run; press twice to confirm"); resetCaption?.destroy();
          });
          return;
        }
        clearRun(); if (this.multiplayerMode) multiplayer.leave(); this.scene.start("StartScene");
      });
    }
    this.input.on("pointerdown", this.handlePointer, this);
    this.input.on("pointermove", this.handleHover, this);
    const clearHover = () => { this.hovered = null; this.drawSelection(); this.input.setDefaultCursor("default"); };
    this.input.on("gameout", clearHover);
    this.input.keyboard?.on("keydown-B", this.battleKey, this);
    this.input.keyboard?.on("keydown-ESC", this.clearSelection, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off("pointerdown", this.handlePointer, this);
      this.input.off("pointermove", this.handleHover, this);
      this.input.off("gameout", clearHover);
      this.input.keyboard?.off("keydown-B", this.battleKey, this);
      this.input.keyboard?.off("keydown-ESC", this.clearSelection, this);
      this.pieces.forEach(view => view.destroy()); this.opponents.forEach(view => view.destroy());
      this.input.setDefaultCursor("default");
    });
    if (this.multiplayerMode) {
      this.networkUnsubscribe = multiplayer.subscribe(event => {
        if (event.type === "state") {
          const run = multiplayer.match!.own.run;
          if (!run) return;
          this.lockedReady = multiplayer.match!.own.ready;
          if (run.phase === "battle") { this.scene.start("BattleScene", { run, online: true }); return; }
          if (!this.busy) { this.run = run; this.syncPieces(); if (run.phase === "reward") this.refreshRewardCards(); }
          this.syncOpponents();
        }
        this.refreshMultiplayerStatus();
      });
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.networkUnsubscribe?.());
      this.refreshMultiplayerStatus();
    } else setGameStatus("ready", this.run.phase === "reward" ? "Reward ready. Choose your next elemental." : "Board ready. Arrange or merge your team.");
  }
  update(time: number): void {
    this.pieces.forEach(view => view.update(time)); this.cards.forEach(card => card.update(time));
    this.opponents.forEach(view => view.update(time));
    if (this.selected && !this.busy) this.drawSelection(this.motion ? time : 0);
  }
  private drawControls(): void {
    if (this.run.phase === "reward") {
      icon(this, "gift", 40).setPosition(stageX(768), stageY(746)).setDepth(620);
      this.refreshRewardCards();
    } else this.battleButton = new BattleButton(this, 768, 923, () => this.requestBattle(), this.multiplayerMode ? "Ready for battle" : undefined);
  }
  private refreshRewardCards(): void {
    const offers = this.multiplayerMode ? multiplayer.match!.own.rewards : rewardOffers(this.run);
    if (this.cards.length === offers.length && offers.every((offer, i) =>
      offer.element === this.cards[i].piece.element && offer.tier === this.cards[i].piece.tier)) {
      this.cards.forEach(card => card.setEnabled(true));
      return;
    }
    // A merge can raise the gift range or open space for new element choices.
    this.cards.forEach(card => card.destroy());
    this.cards = offers.map((reward, i) => {
      const x = 768 + (i - (offers.length - 1) / 2) * 224;
      const card = new PortraitCard(this, x, 894, reward, () => this.takeReward(reward, card), 208, 216, 90 * i);
      return card;
    });
  }
  private takeReward(reward: Reward, card: PortraitCard): void {
    if (this.busy || this.run.phase !== "reward") return;
    this.busy = true; this.cards.forEach(other => other.disable());
    this.drawSelection();
    gameAudio.unlock(); gameAudio.reward();
    if (this.multiplayerMode) {
      void multiplayer.reward(reward).then(match => this.animateReward(reward, card, match.own.run!)).catch(error => this.commandFailed(error));
    } else this.animateReward(reward, card, claimReward(this.run, reward));
  }
  private animateReward(reward: Reward, card: PortraitCard, next: Run): void {
    const index = next.playerBoard.cells.findIndex((piece, i) => piece &&
      (piece.id !== this.run.playerBoard.cells[i]?.id || piece.tier !== this.run.playerBoard.cells[i]?.tier));
    const piece = next.playerBoard.cells[index]!;
    if (!this.multiplayerMode) saveRun(next); // Save the accepted choice before its cosmetic flight begins.
    if (!this.motion) { this.scene.restart({ run: next, arrivingId: piece.id, online: this.multiplayerMode }); return; }
    const from = card.focusPoint(), foot = project({ x: index % 12, y: Math.floor(index / 12) }, this.ownLayout);
    const to = { x: foot.x, y: foot.y - 16 };
    const occupancy = reward.tier < 3 ? 0.38 : reward.tier < 5 ? 0.62 : 0.84;
    const startSize = 216 * 0.64 / occupancy, endSize = pieceMetrics(reward).size;
    const flyer = this.add.image(from.x, from.y + 216 * 0.32, characterTexture(reward))
      .setOrigin(0.5, 0.9).setDisplaySize(startSize, startSize).setDepth(680);
    this.cards.forEach(other => {
      this.tweens.killTweensOf(other.root);
      this.tweens.add({ targets: other.root, alpha: 0, scale: 0.92, duration: 220, ease: "Cubic.In" });
    });
    const start = { x: flyer.x, y: flyer.y };
    let lastTrail = 0;
    this.tweens.addCounter({ from: 0, to: 1, duration: 620, ease: "Sine.InOut",
      onUpdate: tween => {
        const t = tween.getValue()!;
        flyer.setPosition(Phaser.Math.Linear(start.x, to.x, t), Phaser.Math.Linear(start.y, to.y, t) - Math.sin(t * Math.PI) * 125);
        const size = Phaser.Math.Linear(startSize, endSize, t); flyer.setDisplaySize(size, size);
        if (tween.elapsed - lastTrail > 35) {
          lastTrail = tween.elapsed; this.effects.particle(reward.element, flyer.x, flyer.y - size * occupancy / 2);
        }
      }, onComplete: () => {
        flyer.destroy(); this.scene.restart({ run: next, arrivingId: piece.id, online: this.multiplayerMode });
      } });
  }
  private syncPieces(): void {
    const alive = new Set(this.run.playerBoard.cells.filter(piece => piece !== null).map(piece => piece.id));
    this.pieces.forEach((view, id) => { if (!alive.has(id)) { view.destroy(); this.pieces.delete(id); } });
    this.run.playerBoard.cells.forEach((piece, index) => {
      if (!piece) return;
      const pos = { x: index % 12, y: Math.floor(index / 12) }, p = project(pos, this.ownLayout);
      let view = this.pieces.get(piece.id);
      if (view && view.piece.tier !== piece.tier) { view.destroy(); view = undefined; }
      if (!view) { view = new PieceView(this, piece, p.x, p.y, pieceDepth(pos), false, this.motion); this.pieces.set(piece.id, view); }
      view.face(this.ownSide === "player" ? -1 : 1);
      view.root.setPosition(p.x, p.y).setDepth(pieceDepth(pos));
    });
    this.drawSelection();
  }
  private syncOpponents(): void {
    const board = multiplayer.match?.other.run?.playerBoard;
    if (!board) return;
    const latest = this.remoteQueue.at(-1) ?? this.remoteTarget ?? this.remoteBoard;
    if (latest && this.sameBoard(latest, board)) return;
    this.remoteQueue.push(board);
    this.playRemoteUpdate();
  }
  private sameBoard(a: Board, b: Board): boolean {
    return a.width === b.width && a.height === b.height && a.cells.every((piece, i) =>
      piece?.id === b.cells[i]?.id && piece?.tier === b.cells[i]?.tier && piece?.element === b.cells[i]?.element);
  }
  private playRemoteUpdate(): void {
    if (this.remoteAnimating) return;
    const next = this.remoteQueue.shift();
    if (!next) return;
    const previous = this.remoteBoard;
    this.remoteTarget = next;
    this.remoteAnimating = true;
    if (previous && this.motion && previous.width === next.width && previous.height === next.height) {
      const changed = previous.cells.flatMap((piece, i) =>
        piece?.id !== next.cells[i]?.id || piece?.tier !== next.cells[i]?.tier ? [i] : []);
      if (changed.length === 2) {
        for (const indices of [changed, [...changed].reverse()]) {
          const [a, b] = indices.map(index => ({ x: index % next.width, y: Math.floor(index / next.width) }));
          const result = applyBoardAction(previous, a, b);
          if (!this.sameBoard(result.board, next)) continue;
          if (result.action.type === "merge") this.animateMerge(result.action, next, true);
          else this.animateSwap(result.action, next, true);
          return;
        }
      }
    }
    this.finishRemoteUpdate(next);
  }
  private finishRemoteUpdate(board: Board, continueQueue = true): void {
    this.remoteBoard = board;
    this.remoteTarget = undefined;
    const alive = new Set(board.cells.flatMap(piece => piece ? [piece.id] : []));
    this.opponents.forEach((view, id) => { if (!alive.has(id)) { view.destroy(); this.opponents.delete(id); } });
    const side = this.ownSide === "player" ? "opponent" : "player";
    board.cells.forEach((piece, index) => {
      if (!piece) return;
      const pos = { x: index % board.width, y: Math.floor(index / board.width) }, p = project(pos, layoutFor(this, side));
      let view = this.opponents.get(piece.id);
      if (view && view.piece.tier !== piece.tier) { view.destroy(); view = undefined; }
      if (!view) { view = new PieceView(this, piece, p.x, p.y, pieceDepth(pos), false, this.motion); this.opponents.set(piece.id, view); view.appear(); }
      view.face(side === "player" ? -1 : 1); view.root.setPosition(p.x, p.y).setDepth(pieceDepth(pos));
      view.setReady(Boolean(multiplayer.match?.other.ready));
    });
    this.remoteAnimating = !continueQueue;
    if (continueQueue) this.playRemoteUpdate();
  }
  private remoteViewAt(pos: GridPos): PieceView | undefined {
    const piece = this.remoteBoard && getCell(this.remoteBoard, pos);
    return piece ? this.opponents.get(piece.id) : undefined;
  }
  private viewAt(pos: GridPos): PieceView | undefined {
    const piece = getCell(this.run.playerBoard, pos); return piece ? this.pieces.get(piece.id) : undefined;
  }
  private clearSelection(): void {
    if (this.busy) return;
    this.selected = null; this.pieces.forEach(view => view.select(false)); this.drawSelection();
  }
  private drawSelection(time = 0): void {
    const g = this.selection.clear();
    const editable = !this.busy && !this.lockedReady && this.run.phase !== "battle" && !isArtLayoutCapture;
    const mergeable = editable ? mergeablePieceIds(this.run.playerBoard, this.selected) : new Set<string>();
    this.pieces.forEach((view, id) => { view.hover(false); view.setMergeable(mergeable.has(id)); });
    if (!editable) return;
    if (this.hovered) this.viewAt(this.hovered)?.hover(true);
    const selectedPiece = this.selected && getCell(this.run.playerBoard, this.selected);
    if (selectedPiece) {
      this.run.playerBoard.cells.forEach((piece, index) => {
        const pos = { x: index % this.run.playerBoard.width, y: Math.floor(index / this.run.playerBoard.width) };
        if (!piece || !mergeable.has(piece.id)) return;
        const p = project(pos, this.ownLayout);
        g.fillStyle(0xb9e39d, 0.25 + Math.sin(time / 220) * 0.07).lineStyle(2, 0xeaffb5, 0.85);
        drawDiamond(g, p.x, p.y, this.ownLayout.tileWidth - 3, this.ownLayout.tileHeight - 3); g.fillPath().strokePath();
      });
    }
    for (const [pos, chosen] of [[this.hovered, false], [this.selected, true]] as const) {
      if (!pos) continue;
      const p = project(pos, this.ownLayout);
      g.fillStyle(0xffdf87, chosen ? 0.44 : 0.2);
      drawDiamond(g, p.x, p.y, this.ownLayout.tileWidth - 2, this.ownLayout.tileHeight - 2); g.fillPath();
      if (chosen) g.lineStyle(3, 0xffefa7).strokePath();
    }
  }
  private handleHover(pointer: Phaser.Input.Pointer): void {
    if (this.run.phase === "battle" || this.busy || this.lockedReady) return;
    if (pointer.y > stageY(800) || (pointer.y < stageY(180) && pointer.x > stageX(1250))) {
      this.hovered = null; this.drawSelection(); this.input.setDefaultCursor("default"); return;
    }
    const hovered = hitBoardTarget(pointer.x, pointer.y, this.run.playerBoard, this.ownLayout);
    if (sameCell(hovered, this.hovered)) return;
    this.hovered = hovered; this.drawSelection(); this.input.setDefaultCursor(hovered ? "pointer" : "default");
  }
  private handlePointer(pointer: Phaser.Input.Pointer): void {
    if (this.busy || this.lockedReady || this.run.phase === "battle" || pointer.y > stageY(800) || (pointer.y < stageY(180) && pointer.x > stageX(1250))) return;
    const pos = hitBoardTarget(pointer.x, pointer.y, this.run.playerBoard, this.ownLayout);
    if (!pos) { this.clearSelection(); return; }
    if (!this.selected) {
      gameAudio.unlock(); gameAudio.selection();
      this.selected = pos; this.viewAt(pos)?.select(true); this.drawSelection(); return;
    }
    if (sameCell(this.selected, pos)) { this.clearSelection(); return; }
    const from = this.selected;
    const result = applyBoardAction(this.run.playerBoard, from, pos);
    this.busy = true; this.selected = this.hovered = null; this.drawSelection();
    this.battleButton?.setEnabled(false);
    this.cards.forEach(card => card.setEnabled(false));
    this.pieces.forEach(view => view.select(false));
    if (this.multiplayerMode) {
      void multiplayer.arrange(from, pos).then(match => this.presentBoardAction(result.action, match.own.run!.playerBoard)).catch(error => this.commandFailed(error));
    } else this.presentBoardAction(result.action, result.board);
  }
  private presentBoardAction(action: BoardAction, board: Board): void {
    if (!this.motion) {
      if (action.type === "merge") gameAudio.merge(action.newTier);
      this.finishAction(board, action.type === "merge" ? `Merged to tier ${action.newTier}!` : "Moved."); return;
    }
    if (action.type === "merge") this.animateMerge(action, board);
    else this.animateSwap(action, board);
  }

  private animateSwap(action: Extract<BoardAction, { type: "swap" }>, next: Board, remote = false): void {
    const viewAt = (pos: GridPos) => remote ? this.remoteViewAt(pos) : this.viewAt(pos);
    const layout = remote ? layoutFor(this, this.ownSide === "player" ? "opponent" : "player") : this.ownLayout;
    const finish = () => remote ? this.finishRemoteUpdate(next) : this.finishAction(next, "Moved.");
    const moves = [{ view: viewAt(action.a), to: action.b }, { view: viewAt(action.b), to: action.a }]
      .filter((move): move is { view: PieceView; to: GridPos } => !!move.view);
    if (!moves.length) { finish(); return; }
    let left = moves.length;
    moves.forEach(({ view, to }, index) => {
      view.freeze(); view.root.setDepth(500 + index);
      const from = { x: view.root.x, y: view.root.y }, p = project(to, layout);
      this.tweens.addCounter({ from: 0, to: 1, duration: 380, ease: "Sine.InOut",
        onUpdate: tween => {
          const t = tween.getValue()!;
          view.root.setPosition(Phaser.Math.Linear(from.x, p.x, t), Phaser.Math.Linear(from.y, p.y, t));
          view.lift(Math.sin(t * Math.PI) * (index ? 38 : 60));
        }, onComplete: () => { view.land(); if (--left === 0) finish(); } });
    });
  }
  private animateMerge(action: Extract<BoardAction, { type: "merge" }>, next: Board, remote = false): void {
    const source = remote ? this.remoteViewAt(action.from)! : this.viewAt(action.from)!;
    const destination = remote ? this.remoteViewAt(action.to)! : this.viewAt(action.to)!;
    const layout = remote ? layoutFor(this, this.ownSide === "player" ? "opponent" : "player") : this.ownLayout;
    source.freeze(); destination.freeze(); source.root.setDepth(500);
    const from = { x: source.root.x, y: source.root.y }, target = project(action.to, layout);
    let lastParticle = 0;
    this.tweens.add({ targets: source.body, scaleX: 1.26, scaleY: 0.7, y: -8, duration: 110, ease: "Sine.In",
      onComplete: () => this.tweens.addCounter({ from: 0, to: 1, duration: 340, ease: "Cubic.In",
        onUpdate: tween => {
          const t = tween.getValue()!;
          source.root.setPosition(Phaser.Math.Linear(from.x, target.x, t), Phaser.Math.Linear(from.y, target.y, t));
          source.lift(Math.sin(t * Math.PI) * 85);
          source.body.setScale(1 - t * 0.42, 1 + Math.sin(t * Math.PI) * 0.15 - t * 0.42);
          if (tween.elapsed - lastParticle > 28) {
            lastParticle = tween.elapsed; const p = source.focusPoint(); this.effects.particle(action.element, p.x, p.y);
          }
        }, onComplete: () => {
          if (remote) {
            this.finishRemoteUpdate(next, false);
          } else {
            this.run = { ...this.run, playerBoard: next }; if (!this.multiplayerMode) saveRun(this.run); this.syncPieces();
          }
          const upgraded = remote ? this.remoteViewAt(action.to)! : this.viewAt(action.to)!;
          this.effects.mergeBurst(action.element, target.x, target.y, 1.7);
          gameAudio.merge(action.newTier);
          upgraded.appear();
          this.time.delayedCall(540, () => {
            if (remote) { this.remoteAnimating = false; this.playRemoteUpdate(); }
            else this.finishAction(next, `Merged to tier ${action.newTier}!`);
          });
        } }) });
  }
  private finishAction(board: Board, message: string): void {
    this.run = this.multiplayerMode ? multiplayer.match!.own.run! : { ...this.run, playerBoard: board };
    if (!this.multiplayerMode) saveRun(this.run); this.syncPieces();
    this.busy = false; this.battleButton?.setEnabled(true);
    this.drawSelection();
    this.dock?.destroy(); if (!isArtLayoutCapture) this.dock = teamDock(this, board);
    if (this.run.phase === "reward") this.refreshRewardCards();
    setGameStatus("ready", this.run.phase === "reward" ? `Reward ready. ${message} Choose your next elemental.` : message);
  }
  private requestBattle(): void {
    if (this.busy || this.run.phase !== "board" || this.lockedReady) return;
    if (this.multiplayerMode) {
      this.busy = true; this.battleButton?.setEnabled(false);
      this.drawSelection();
      void multiplayer.ready().then(() => { this.busy = false; this.refreshMultiplayerStatus(); }).catch(error => this.commandFailed(error));
      return;
    }
    this.busy = true; this.run = beginBattle(this.run); saveRun(this.run);
    gameAudio.unlock();
    this.scene.start("BattleScene", { run: this.run });
  }
  private battleKey(): void {
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && (focused.isContentEditable || focused.matches("input, textarea, select"))) return;
    this.requestBattle();
  }
  private commandFailed(error: Error): void {
    this.busy = false;
    if (multiplayer.match?.own.run) this.run = multiplayer.match.own.run;
    this.syncPieces(); this.refreshMultiplayerStatus();
    if (this.run.phase === "reward") this.refreshRewardCards();
    multiplayer.fail(error.message);
  }
  private refreshMultiplayerStatus(): void {
    if (!this.multiplayerMode) return;
    this.lockedReady = Boolean(multiplayer.match?.own.ready);
    if (multiplayer.match) this.teams?.refresh(multiplayer.match);
    this.pieces.forEach(view => view.setReady(this.lockedReady));
    this.opponents.forEach(view => view.setReady(Boolean(multiplayer.match?.other.ready)));
    if (this.lockedReady) { this.selected = this.hovered = null; this.pieces.forEach(view => view.select(false)); this.input.setDefaultCursor("default"); }
    this.drawSelection();
    this.battleButton?.setEnabled(!this.busy && !this.lockedReady && multiplayer.status === "connected");
    const status = this.lockedReady ? "Waiting for your opponent to click Battle." : this.run.phase === "reward" ? "Reward ready. Choose your next elemental." : "Board ready. Arrange your team, then click Battle.";
    if (!multiplayer.error) setGameStatus("ready", status);
  }
}
